/**
 * Timesheets API
 * Handles all timesheet-related API calls
 */
import apiClient from './apiClient';

export interface Timesheet {
  id: number;
  employee_id: number;
  employee_name: string | null;
  employee_email: string | null;
  employee_code: string | null;
  department_id: number | null;
  department_name: string | null;
  client_name: string | null;
  project_id: number;
  project_name: string | null;
  project_type: string | null;
  task_type_id: number;
  task_type_name: string | null;
  date: string;
  hours: number;
  description: string | null;
  status: 'submitted' | 'approved' | 'rejected';
  reject_reason: string | null;
  employee_category: string | null;
  employee_practice: string | null;
  project_manager_name: string | null;
  is_late_entry: boolean;
  late_entry_request_id: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface TimesheetsListResponse {
  timesheets: Timesheet[];
  total: number;
  page?: number;
  per_page?: number;
  pages?: number;
  status_counts?: {
    submitted: number;
    approved: number;
    rejected: number;
  };
  all_count?: number;
}

export interface TimesheetsQueryParams {
  status?: string;
  employee_id?: number;
  project_id?: number;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
  department_id?: number;
  manager_id?: number;
  task_type_id?: number;
  billable?: string;
  employee_category?: string;
  employee_practice?: string;
  active_inactive?: string;
  search?: string;
  project_manager_id?: number;
  // Column-specific searches
  search_employee?: string;
  search_project?: string;
  search_manager?: string;
  search_project_manager?: string;
  [key: string]: string | number | undefined;
}

// Group By types
export type GroupByField =
  | 'project'
  | 'employee'
  | 'date'
  | 'department'
  | 'manager'
  | 'project_manager'
  | 'status'
  | 'tasks'
  | 'billable'
  | 'project_type'
  | 'employee_category'
  | 'employee_practice'
  | 'active_inactive'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year';

export interface GroupNode {
  key: string;
  label: string;
  field: string;
  field_value: string | null;
  count: number;
  total_hours: number;
  subgroups?: GroupNode[];
}

export interface GroupedTimesheetsResponse {
  groups: GroupNode[];
  total: number;
  grand_total_hours: number;
  group_by: string[];
}

export interface GroupedTimesheetsParams {
  group_by: string;
  scope: 'all' | 'team';
  status?: string;
  employee_id?: number;
  project_id?: number;
  start_date?: string;
  end_date?: string;
  department_id?: number;
  manager_id?: number;
  task_type_id?: number;
  billable?: string;
  employee_category?: string;
  employee_practice?: string;
  active_inactive?: string;
  search?: string;
  project_manager_id?: number;
  // Column-specific searches
  search_employee?: string;
  search_project?: string;
  search_manager?: string;
  search_project_manager?: string;
  [key: string]: string | number | undefined;
}

export interface WeeklyViewResponse {
  week_start: string;
  week_end: string;
  entries: Record<string, Timesheet[]>;
  daily_totals: Record<string, number>;
  week_total: number;
}

export interface TimesheetSummaryResponse {
  hours_this_week: number;
  hours_this_month: number;
  pending_timesheets: number;
  approved_timesheets: number;
  rejected_timesheets: number;
}

export interface CreateTimesheetData {
  project_id: number;
  task_type_id: number;
  date: string;
  hours: number;
  description?: string;
}

export interface UpdateTimesheetData {
  task_type_id?: number;
  hours?: number;
  description?: string;
}

export const timesheetsApi = {
  /**
   * Get timesheets (filtered by role)
   */
  getTimesheets: async (params?: TimesheetsQueryParams): Promise<TimesheetsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', String(params.status));
    if (params?.employee_id) queryParams.append('employee_id', String(params.employee_id));
    if (params?.project_id) queryParams.append('project_id', String(params.project_id));
    if (params?.start_date) queryParams.append('start_date', String(params.start_date));
    if (params?.end_date) queryParams.append('end_date', String(params.end_date));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));

    const query = queryParams.toString();
    return apiClient.get<TimesheetsListResponse>(`/timesheets${query ? `?${query}` : ''}`);
  },

  /**
   * Get current employee's timesheets
   */
  getMyTimesheets: async (params?: Omit<TimesheetsQueryParams, 'employee_id'>): Promise<TimesheetsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', String(params.status));
    if (params?.project_id) queryParams.append('project_id', String(params.project_id));
    if (params?.start_date) queryParams.append('start_date', String(params.start_date));
    if (params?.end_date) queryParams.append('end_date', String(params.end_date));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));
    if (params?.task_type_id) queryParams.append('task_type_id', String(params.task_type_id));

    const query = queryParams.toString();
    return apiClient.get<TimesheetsListResponse>(`/timesheets/my-timesheets${query ? `?${query}` : ''}`);
  },

  /**
   * Get weekly view for current user
   */
  getWeeklyView: async (weekStart?: string): Promise<WeeklyViewResponse> => {
    const queryParams = new URLSearchParams();
    if (weekStart) queryParams.append('week_start', weekStart);

    const query = queryParams.toString();
    return apiClient.get<WeeklyViewResponse>(`/timesheets/weekly-view${query ? `?${query}` : ''}`);
  },

  /**
   * Get timesheet summary for current user
   */
  getSummary: async (): Promise<TimesheetSummaryResponse> => {
    return apiClient.get<TimesheetSummaryResponse>('/timesheets/summary');
  },

  /**
   * Get a specific timesheet by ID
   */
  getTimesheet: async (timesheetId: number): Promise<{ timesheet: Timesheet }> => {
    return apiClient.get<{ timesheet: Timesheet }>(`/timesheets/${timesheetId}`);
  },

  /**
   * Create a new timesheet entry
   */
  createTimesheet: async (data: CreateTimesheetData): Promise<{ message: string; timesheet: Timesheet }> => {
    return apiClient.post('/timesheets', data);
  },

  /**
   * Update a timesheet entry
   */
  updateTimesheet: async (timesheetId: number, data: UpdateTimesheetData): Promise<{ message: string; timesheet: Timesheet }> => {
    return apiClient.put(`/timesheets/${timesheetId}`, data);
  },

  /**
   * Delete a timesheet entry
   */
  deleteTimesheet: async (timesheetId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/timesheets/${timesheetId}`);
  },

  /**
   * Submit a draft timesheet for approval
   */
  submitTimesheet: async (timesheetId: number): Promise<{ message: string; timesheet: Timesheet }> => {
    return apiClient.put(`/timesheets/${timesheetId}/submit`);
  },

  /**
   * Get timesheets pending approval (Manager/PMO/SuperAdmin)
   */
  getPendingApproval: async (): Promise<TimesheetsListResponse> => {
    return apiClient.get<TimesheetsListResponse>('/timesheets/pending-approval');
  },

  /**
   * Approve a timesheet (Manager/PMO/SuperAdmin)
   */
  approveTimesheet: async (timesheetId: number): Promise<{ message: string; timesheet: Timesheet }> => {
    return apiClient.put(`/timesheets/${timesheetId}/approve`);
  },

  /**
   * Reject a timesheet (Manager/PMO/SuperAdmin)
   */
  rejectTimesheet: async (timesheetId: number, reason?: string): Promise<{ message: string; timesheet: Timesheet }> => {
    return apiClient.put(`/timesheets/${timesheetId}/reject`, { reason });
  },

  /**
   * Bulk approve timesheets (Manager/PMO/SuperAdmin)
   */
  bulkApprove: async (timesheetIds: number[]): Promise<{ message: string; approved_count: number; errors?: string[] }> => {
    return apiClient.post('/timesheets/bulk-approve', { timesheet_ids: timesheetIds });
  },

  /**
   * Get grouped timesheets (PMO/Manager)
   */
  getGroupedTimesheets: async (params: GroupedTimesheetsParams): Promise<GroupedTimesheetsResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    return apiClient.get<GroupedTimesheetsResponse>(`/timesheets/grouped?${queryParams.toString()}`);
  },

  /**
   * Export timesheets as XLSX (PMO / Manager / All Timesheets page).
   *
   * Forwards every defined param so the downloaded file matches the filters
   * and visible columns currently shown in the UI. The full filter set
   * supported by the backend (status, dates, project, employee, department,
   * manager, project manager, task type, billable, category/practice,
   * active/inactive, search, and per-column `search_*` chips) plus the
   * `columns` selection are all carried through.
   */
  exportTimesheets: async (params: {
    scope: 'all' | 'team';
    group_by?: string;
    columns?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    project_id?: number;
    employee_id?: number;
    department_id?: number;
    manager_id?: number;
    project_manager_id?: number;
    task_type_id?: number;
    billable?: string;
    employee_category?: string;
    employee_practice?: string;
    active_inactive?: string;
    search?: string;
    [key: string]: any;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    queryParams.append('scope', params.scope);
    queryParams.append('format', 'xlsx');
    Object.entries(params).forEach(([key, value]) => {
      if (key === 'scope') return; // already appended above
      if (value === undefined || value === null || value === '') return;
      queryParams.append(key, String(value));
    });

    return apiClient.getBlob(`/timesheets/export?${queryParams.toString()}`);
  },

  /**
   * Export current employee's timesheets as CSV/XLSX
   */
  exportMyTimesheets: async (params: {
    format?: 'csv' | 'xlsx';
    status?: string;
    start_date?: string;
    end_date?: string;
    project_id?: number;
    columns?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    queryParams.append('format', params.format || 'xlsx');
    if (params.status) queryParams.append('status', params.status);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.project_id) queryParams.append('project_id', String(params.project_id));
    if (params.columns) queryParams.append('columns', params.columns);

    return apiClient.getBlob(`/timesheets/my-timesheets/export?${queryParams.toString()}`);
  },

  /**
   * Get timesheets with expanded filters (for detail rows in grouped view)
   */
  getFilteredTimesheets: async (params: TimesheetsQueryParams): Promise<TimesheetsListResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const query = queryParams.toString();
    return apiClient.get<TimesheetsListResponse>(`/timesheets${query ? `?${query}` : ''}`);
  },
};

export default timesheetsApi;
